import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import {
  MateriaMedicaSection,
  RepertoryPagination,
  RepertoryRemedy,
  RepertoryRubricResult,
  RepertorySearchResponse,
  RepertorySource,
  RepertoryStatus,
} from '../../models/repertory.model';

@Injectable({
  providedIn: 'root',
})
export class RepertoryService {
  private apiUrl: string;

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.apiUrl = this.apiService.getBaseUrl();
  }

  private authHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
      }),
    };
  }

  getStatus(): Observable<{ success: boolean; data: RepertoryStatus }> {
    return this.http.get<{ success: boolean; data: RepertoryStatus }>(
      `${this.apiUrl}/repertory/status`,
      this.authHeaders(),
    );
  }

  listRepertories(): Observable<{ success: boolean; data: RepertorySource[] }> {
    return this.http.get<{ success: boolean; data: RepertorySource[] }>(
      `${this.apiUrl}/repertory/repertories`,
      this.authHeaders(),
    );
  }

  listMateriaMedicas(): Observable<{ success: boolean; data: RepertorySource[] }> {
    return this.http.get<{ success: boolean; data: RepertorySource[] }>(
      `${this.apiUrl}/repertory/materia-medicas`,
      this.authHeaders(),
    );
  }

  listRemedies(query = '', limit = 100): Observable<{ success: boolean; data: RepertoryRemedy[] }> {
    let params = new HttpParams();
    if (query.trim()) {
      params = params.set('q', query.trim());
    }
    params = params.set('limit', String(limit));

    return this.http.get<{ success: boolean; data: RepertoryRemedy[] }>(
      `${this.apiUrl}/repertory/remedies`,
      { ...this.authHeaders(), params },
    );
  }

  searchRepertory(options: {
    repertory: string;
    symptom: string;
    page?: number;
    minWeight?: number;
    remedy?: string;
  }): Observable<RepertorySearchResponse<RepertoryRubricResult>> {
    let params = new HttpParams()
      .set('repertory', options.repertory)
      .set('symptom', options.symptom);

    if (options.page) {
      params = params.set('page', String(options.page));
    }
    if (options.minWeight !== undefined) {
      params = params.set('min_weight', String(options.minWeight));
    }
    if (options.remedy?.trim()) {
      params = params.set('remedy', options.remedy.trim());
    }

    return this.http.get<RepertorySearchResponse<RepertoryRubricResult>>(
      `${this.apiUrl}/repertory/search`,
      { ...this.authHeaders(), params },
    );
  }

  searchMateriaMedica(options: {
    materiaMedica: string;
    symptom: string;
    page?: number;
    remedy?: string;
  }): Observable<RepertorySearchResponse<MateriaMedicaSection>> {
    let params = new HttpParams()
      .set('materia_medica', options.materiaMedica)
      .set('symptom', options.symptom);

    if (options.page) {
      params = params.set('page', String(options.page));
    }
    if (options.remedy?.trim()) {
      params = params.set('remedy', options.remedy.trim());
    }

    return this.http.get<RepertorySearchResponse<MateriaMedicaSection>>(
      `${this.apiUrl}/repertory/search-mm`,
      { ...this.authHeaders(), params },
    );
  }
}
